import * as tf from 'tf-custom';
import { RecorderService } from './utils/RecorderService';
import {
  ADJUSTMENT_KEYS,
  LOUDNESS_ADJUSTMENTS,
  MIX_ADJUSTMENTS,
  OCTAVE_ADJUSTMENTS,
} from './constants';
import { ERROR_STATES } from './errors';
import { AudioContext } from 'standardized-audio-context';

export type Loudness = typeof LOUDNESS_ADJUSTMENTS[number];
export type Mix = typeof MIX_ADJUSTMENTS[number];
export type Octave = typeof OCTAVE_ADJUSTMENTS[number];

export interface AudioAdjustments {
  [ADJUSTMENT_KEYS.LOUDNESS]: Loudness;
  [ADJUSTMENT_KEYS.MIX]?: Mix;
  [ADJUSTMENT_KEYS.OCTAVE]: Octave;
}

export interface AudioAdjustmentMap {
  [key: string]: {
    [key: string]: AudioAdjustments;
  };
}

export interface AudioDataItem {
  audioAdjusted?: Map<string, Float32Array>;
  audio?: Float32Array;
  shape?: number[];
}

export interface AudioFeatures {
  // tslint:disable-next-line: no-any
  f0_hz: any; // [] | Tensor; TODO: find tensor type
  // tslint:disable-next-line: no-any
  loudness_db: any;
  confidences?: number[];
  originalRecordedBufferLength?: number;
}

export interface AudioCollection {
  [key: string]: AudioDataItem;
}

export interface AllAudioData {
  [key: string]: AudioCollection;
}

export interface AudioRegion {
  start: number;
  stop: number;
  instrument?: string;
}

export interface FooterContent {
  title: string;
  description: string;
}

export interface MusicalEntity {
  color?: string;
  descriptor?: string;
  id?: string;
  isLoaded?: boolean;
  label?: string;
  tooltip?: string;
}

export interface RecordingData {
  src: string;
  type: string;
  duration?: number;
}

export interface RecordMenuItem {
  id: string;
  noRecording: {
    descriptor: string;
    icon: JSX.Element;
  };
  hasRecording: {
    descriptor: string;
    icon: JSX.Element;
  };
}

export interface InstructionText {
  post?: string;
  pre?: string;
  primary: string | Function;
}

export interface Arrow {
  headCoordinates: number[];
  headPath: number[][];
  linePath: number[][];
  mobileArrowPosition?: string;
}

export interface Instruction {
  arrowPosition?: string;
  text: InstructionText;
}

export interface Instrument {
  color: string;
  backgroundColor: string;
  descriptor: string;
  label: string;
  tooltips: Tooltip[];
}

export interface PlayerTimer {
  current: number;
  elapsed: number;
  offset: number;
  paused: number;
  start?: number;
}

export interface PlayerContextProps {
  activePlayerKey?: string;
  setActivePlayerKey: Function;
  setAudioData: Function;
}

// statuses for overall transformations
export enum TRANSFORMATION_PROGRESS_STATUS {
  INITIAL = 'initial',
  // UPLOADING_TO_CLOUD = 'upload-cloud',
  // UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  TRANSFORMING = 'transforming',
  DONE = 'done',
}

// statuses for individual transformation
export enum TRANSFORMATION_PROGRESS {
  PROCESSING = 'processing',
  PENDING = 'pending',
  DONE = 'done',
}

export interface TransformationProgress {
  currentStatus?: TRANSFORMATION_PROGRESS_STATUS;
  outputs?: {
    [key: string]: TRANSFORMATION_PROGRESS;
  };
}

export interface RecordingContextProps {
  cancelRecording: Function;
  hasUserClickedTransform: boolean;
  isRecording: boolean;
  recorderSvc: RecorderService;
  recorderTimeoutId: ReturnType<typeof setTimeout>;
  recordingTimeElapsed: number;
  resetTimer: Function;
  recordingData: RecordingData;
  setHasUserClickedTransform: Function;
  setIsRecording: Function;
  setRecordingData: Function;
  setRecorderSvc: Function;
  setRecorderTimeoutId: Function;
  setRecordingTimeElapsed: Function;
  setTransformationProgress: Function;
  startTimer: Function;
  stopAllRecording: Function;
  transformationProgress: TransformationProgress;

  // new props
  audioCtx: AudioContext;
  memoryCheckSuccess: boolean | null;
  setMemoryCheckSuccess: Function;
  audioFeatures: AudioFeatures | null;
  setAudioFeatures: Function;
  instrumentModels: {
    [key: string]: {
      model: tf.Model | null;
      isLoaded: boolean;
    };
  };
  setInstrumentModels: Function;
}

export interface ErrorContextProps {
  errorState?: ERROR_STATES;
  setErrorState: Function;
}

export interface TooltipCoordinates {
  x: number;
  y: number;
}

export interface Tooltip {
  audioSource?: string;
  content: string;
  coordinates: TooltipCoordinates;
  icon: JSX.Element;
  title: string;
}

export interface TrackingEventData {
  action: string;
  category: string;
  label?: string;
  nonInteraction?: boolean;
  value?: number;
}

export interface TrackingExceptionData {
  description: string;
  fatal?: boolean;
}

export interface YoutubeEvent {
  target: string;
  data: number;
}
